import React, { useEffect, useMemo, useState } from "react";

import { TextField } from "ui/components/TextField/TextField";
import { DatePicker } from "ui/components/DatePicker";
import { CheckBox } from "ui/components/CheckBox";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { ReactSVG } from "react-svg";

import {
  useHandleTextFieldChange,
  useHandleCheckboxChange,
  useHandleDateChange,
  useHandleDropdownChange,
} from "services/forms/hooks";
import { EkoProgram } from "services/api/catalog/cvh/types";
import { getEkoProgramAPI } from "services/api/catalog/cvh/api";
import { classNames, useTranslate } from "helpers";

import { ResidualValueVehicleDetailsFormProps } from "./types";

/** Drugi korak: podaci koji ulaze u izračun, odvojeni od odabira vozila. */
const ResidualValueVehicleDetailsForm: React.FC<ResidualValueVehicleDetailsFormProps> = ({
  show,
  formValues,
  setFormValues,
  errors,
}) => {
  const { t } = useTranslate();
  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleCheckboxChange = useHandleCheckboxChange(setFormValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);

  const [ekoProgram, setEkoProgram] = useState<EkoProgram[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show) return;

    fetchEkoProgram();
  }, [show]);

  const fetchEkoProgram = async () => {
    setIsLoading(true);
    try {
      const data = await getEkoProgramAPI();
      setEkoProgram(data);
    } catch {
      setEkoProgram([]);
    }
    setIsLoading(false);
  };

  const ekoProgramOptions: DropdownOption[] = useMemo(() => {
    return ekoProgram.map((e) => ({
      value: e.id,
      text: e.name,
    }));
  }, [ekoProgram]);

  return (
    <div className="px-4 space-y-4">
    <div className="border-slate-200 border px-3 pt-4 pb-2">
      <p className="text-sm text-slate-700 mb-2 font-bold">Detalji vozila</p>
      <p className="text-sm text-slate-500 mb-3">Popunite detalje vozila kako bi dobili što točniji izračun</p>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {[
          { value: false, label: "Novo vozilo", icon: "car", description: "Vozilo koje nije bilo registrirano" },
          { value: true, label: "Rabljeno vozilo", icon: "car-used", description: "Vozilo koje je prethodno bilo u upotrebi" },
        ].map((option) => {
          const isSelected = formValues.usedVehicle === option.value;
          return (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => setFormValues((prev) => ({ ...prev, usedVehicle: option.value }))}
              className={classNames(
                "relative flex flex-col items-center p-6 rounded-sm border transition-all duration-200 cursor-pointer",
                isSelected
                  ? "border-amber-500 bg-amber-50 "
                  : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
              )}
            >
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <ReactSVG
                    src="/media/icons/check.svg"
                    className="w-5 h-5 fill-amber-500"
                  />
                </div>
              )}
              {/* <div
                  className={classNames(
                    "w-14 h-14 rounded-xl flex items-center justify-center mb-4",
                    isSelected ? "bg-amber-100" : "bg-slate-100"
                  )}
                >
                  <ReactSVG
                    src={`/media/icons/${option.icon}.svg`}
                    className={classNames(
                      "w-7 h-7",
                      isSelected ? "stroke-amber-600" : "stroke-slate-600"
                    )}
                  />
                </div> */}
              <h3
                className={classNames(
                  "text-base font-semibold mb-2",
                  isSelected ? "text-amber-900" : "text-slate-900"
                )}
              >
                {option.label}
              </h3>
              <p
                className={classNames(
                  "text-sm text-center leading-relaxed",
                  isSelected ? "text-amber-700" : "text-slate-500"
                )}
              >
                {option.description}
              </p>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-4 gap-3">

        <div className="mb-3">
          <DatePicker
            name="priceOnDate"
            label={t("admin.fleet.vehicle.residualvalue.priceondate")}
            placeholder={t("admin.fleet.vehicle.residualvalue.priceondate.placeholder")}
            value={formValues.priceOnDate}
            onChange={handleDateChange}
          />
        </div>
        <div className="mb-3">
          <DatePicker
            name="firstRegistrationDate"
            label={t("admin.fleet.vehicle.residualvalue.firstregistrationdate")}
            placeholder={t("admin.fleet.vehicle.residualvalue.firstregistrationdate.placeholder")}
            value={formValues.firstRegistrationDate}
            onChange={handleDateChange}
            error={errors.firstRegistrationDate}
            required={formValues.usedVehicle}
          />
        </div>
        <div className="mb-3">
          <DatePicker
            name="financingStartDate"
            label={t("admin.fleet.vehicle.residualvalue.financingstartdate")}
            placeholder={t("admin.fleet.vehicle.residualvalue.financingstartdate.placeholder")}
            value={formValues.financingStartDate}
            onChange={handleDateChange}
          />
        </div>
        <div className="mb-3">
          <DatePicker
            name="contractEndDate"
            label={t("admin.fleet.vehicle.residualvalue.contractenddate")}
            placeholder={t("admin.fleet.vehicle.residualvalue.contractenddate.placeholder")}
            value={formValues.contractEndDate}
            onChange={handleDateChange}
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="mileageKm"
            label={t("admin.fleet.vehicle.residualvalue.mileagekm")}
            placeholder={t("admin.fleet.vehicle.residualvalue.mileagekm.placeholder")}
            value={formValues.mileageKm ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="km"
            error={errors.mileageKm}
            required={formValues.usedVehicle}
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="mileageAtContractEnd"
            label={t("admin.fleet.vehicle.residualvalue.mileageatcontractend")}
            placeholder={t("admin.fleet.vehicle.residualvalue.mileageatcontractend.placeholder")}
            value={formValues.mileageAtContractEnd ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="km"
          />
        </div>

        <div className="mb-3">
          <TextField
            type="number"
            name="invoicePrice"
            label={t("admin.fleet.vehicle.residualvalue.invoiceprice")}
            placeholder={t("admin.fleet.vehicle.residualvalue.invoiceprice.placeholder")}
            value={formValues.invoicePrice ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="€"
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="ppmvByInvoice"
            label={t("admin.fleet.vehicle.residualvalue.ppmvbyinvoice")}
            placeholder={t("admin.fleet.vehicle.residualvalue.ppmvbyinvoice.placeholder")}
            value={formValues.ppmvByInvoice ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="€"
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="co2"
            label={t("admin.fleet.vehicle.residualvalue.co2")}
            placeholder={t("admin.fleet.vehicle.residualvalue.co2.placeholder")}
            value={formValues.co2 ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="g/km"
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="numberOfSeats"
            label={t("admin.fleet.vehicle.residualvalue.numberofseats")}
            placeholder={t("admin.fleet.vehicle.residualvalue.numberofseats.placeholder")}
            value={formValues.numberOfSeats ?? ""}
            onChange={handleTextFieldChange}
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="electricRange"
            label={t("admin.fleet.vehicle.residualvalue.electricrange")}
            placeholder={t("admin.fleet.vehicle.residualvalue.electricrange.placeholder")}
            value={formValues.electricRange ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="km"
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="enginePower"
            label={t("admin.fleet.vehicle.residualvalue.enginepower")}
            placeholder={t("admin.fleet.vehicle.residualvalue.enginepower.placeholder")}
            value={formValues.enginePower ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="kw"
          />
        </div>
        <div className="mb-3">
          <TextField
            type="number"
            name="engineDisplacement"
            label={t("admin.fleet.vehicle.residualvalue.enginedisplacement")}
            placeholder={t("admin.fleet.vehicle.residualvalue.enginedisplacement.placeholder")}
            value={formValues.engineDisplacement ?? ""}
            onChange={handleTextFieldChange}
            groupAppend="ccm"
          />
        </div>
        <div className="mb-3">
          <Dropdown
            name="ecoProgramId"
            label={t("admin.fleet.vehicle.residualvalue.ecoprogramid")}
            placeholder={t("admin.fleet.vehicle.residualvalue.ecoprogramid.placeholder")}
            value={formValues.ecoProgramId}
            options={ekoProgramOptions}
            onChange={handleDropdownChange}
            isLoading={isLoading}
          />
        </div>
      </div>

      <div className="flex gap-6 pt-2">

        <CheckBox
          name="manualPriceEntry"
          checked={formValues.manualPriceEntry}
          onChange={handleCheckboxChange}
          label={t("admin.fleet.vehicle.residualvalue.manualpriceentry")}
        />
        <CheckBox
          name="ppmvPassengerVehicles"
          checked={formValues.ppmvPassengerVehicles}
          onChange={handleCheckboxChange}
          label={t("admin.fleet.vehicle.residualvalue.ppmvpassengervehicles")}
        />
      </div>
    </div>
    </div>
  );
};

export default ResidualValueVehicleDetailsForm;
